import { ref, shallowRef, watch } from 'vue';

import { WIDGET_MESSAGE_MAX_ATTACHMENTS } from '@support/widget-contracts';

import { refusalFor, uploadAttachment } from '@/api/attachments';
import { attachmentsAvailable } from '@/api/compat';

export interface PendingAttachment {
  key: number;
  file: File;
  /** Set once the bytes are stored, so a send that is retried uploads nothing twice. */
  fileId: string | null;
}

/**
 * The images chosen for the message being written, before it is sent.
 *
 * Nothing is uploaded when a file is chosen — only when the message goes. A
 * visitor who picks a screenshot and then thinks better of it has stored
 * nothing, and a chip can be removed without anything to undo.
 */
export const useAttachments = () => {
  const pending = shallowRef<PendingAttachment[]>([]);

  /** Why the last file chosen was not taken, for the person who chose it. */
  const refusal = ref<string | null>(null);

  let nextKey = 0;

  const add = (files: File[]): void => {
    refusal.value = null;

    for (const file of files) {
      const reason = refusalFor(file);

      if (reason) {
        refusal.value = reason;
      } else if (pending.value.length >= WIDGET_MESSAGE_MAX_ATTACHMENTS) {
        refusal.value = `A message can carry up to ${WIDGET_MESSAGE_MAX_ATTACHMENTS} images.`;
      } else {
        pending.value = [...pending.value, { key: nextKey, file, fileId: null }];
        nextKey += 1;
      }
    }
  };

  const remove = (key: number): void => {
    pending.value = pending.value.filter((item) => item.key !== key);
    refusal.value = null;
  };

  const clear = (): void => {
    pending.value = [];
    refusal.value = null;
  };

  /** Store whatever is not stored yet, and name every file in the order chosen. */
  const upload = async (): Promise<string[]> => {
    const ids: string[] = [];

    for (const item of pending.value) {
      item.fileId ??= await uploadAttachment(item.file);
      ids.push(item.fileId);
    }

    return ids;
  };

  // An API that cannot store a file: what was chosen cannot go, and leaving the
  // chips up would make every further Send fail on them. The words stay typed.
  watch(attachmentsAvailable, (available) => {
    if (!available) {
      pending.value = [];
    }
  });

  return { pending, refusal, add, remove, clear, upload, available: attachmentsAvailable };
};
